<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const createDefaultButton = () => ({
  type: 'QUICK_REPLY',
  text: '',
  url: '',
  phone_number: '',
  example: '',
});

const buildDefaultForm = () => ({
  templateMode: 'standard',
  name: '',
  category: 'UTILITY',
  language: 'es_AR',
  customLanguage: '',
  body: '',
  bodyExamplesText: '',
  footer: '',
  headerType: '',
  headerText: '',
  headerExampleHandle: '',
  headerExamplesText: '',
  catalogButtonText: 'Ver catalogo',
  multiProductButtonText: 'Ver productos',
  buttons: [],
});

const languageOptions = [
  { value: 'es_AR', label: 'Espanol (AR)' },
  { value: 'pt_BR', label: 'Portugues (BR)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'custom', label: 'Custom' },
];

const categoryOptions = [
  { value: 'UTILITY', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY_UTILITY' },
  { value: 'MARKETING', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY_MARKETING' },
  { value: 'AUTHENTICATION', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY_AUTHENTICATION' },
];

const templateModeOptions = [
  { value: 'standard', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE_STANDARD' },
  { value: 'catalog', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE_CATALOG' },
  { value: 'multi_product', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE_MULTI_PRODUCT' },
];

const buttonTypeOptions = [
  { value: 'QUICK_REPLY', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_QUICK_REPLY' },
  { value: 'URL', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_URL' },
  { value: 'PHONE_NUMBER', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_PHONE' },
  { value: 'COPY_CODE', labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_COPY_CODE' },
];

const templates = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const deletingTemplateName = ref('');
const loadError = ref('');
const hasLoaded = ref(false);
const showCreateForm = ref(false);
const editingTemplateId = ref('');
const expandedTemplateName = ref('');
const searchQuery = ref('');
const statusFilter = ref('all');
const categoryFilter = ref('all');
const form = ref(buildDefaultForm());
const createFormRef = ref(null);

const selectedLanguage = computed(() =>
  form.value.language === 'custom'
    ? form.value.customLanguage.trim()
    : form.value.language
);

const bodyExamples = computed(() =>
  form.value.bodyExamplesText
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean)
);

const headerExamples = computed(() =>
  form.value.headerExamplesText
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean)
);

const effectiveButtons = computed(() => {
  if (form.value.templateMode === 'catalog') {
    return form.value.catalogButtonText.trim()
      ? [{ type: 'CATALOG', text: form.value.catalogButtonText.trim() }]
      : [];
  }

  if (form.value.templateMode === 'multi_product') {
    return form.value.multiProductButtonText.trim()
      ? [{ type: 'MPM', text: form.value.multiProductButtonText.trim() }]
      : [];
  }

  return form.value.buttons
    .map(button => ({
      ...button,
      text: button.text.trim(),
      url: button.url.trim(),
      phone_number: button.phone_number.trim(),
      example: button.example.trim(),
    }))
    .filter(button => button.type && button.text);
});

const canCreateTemplate = computed(() => {
  if (!form.value.name.trim() || !selectedLanguage.value || !form.value.body.trim()) {
    return false;
  }

  if (form.value.templateMode === 'catalog') {
    return !!form.value.catalogButtonText.trim();
  }

  if (form.value.templateMode === 'multi_product') {
    return !!form.value.multiProductButtonText.trim();
  }

  return true;
});

const selectedLanguageLabel = computed(() => {
  const preset = languageOptions.find(option => option.value === form.value.language);
  if (form.value.language === 'custom') return selectedLanguage.value || 'Custom';
  return preset?.label || selectedLanguage.value;
});

const previewHeaderLabel = computed(() => {
  if (form.value.headerType === 'TEXT') return form.value.headerText.trim();
  if (form.value.headerType) return form.value.headerType;
  return '';
});

const filteredTemplates = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  return templates.value.filter(template => {
    const name = (template?.name || '').toString().toLowerCase();
    const status = (template?.status || '').toString().toLowerCase();
    const category = (template?.category || '').toString().toLowerCase();
    const kind = templateKind(template).toLowerCase();

    if (query && ![name, status, category, kind].some(value => value.includes(query))) {
      return false;
    }

    if (categoryFilter.value !== 'all' && category !== categoryFilter.value) {
      return false;
    }

    if (statusFilter.value !== 'all' && status !== statusFilter.value) {
      return false;
    }

    return true;
  });
});

const resetForm = () => {
  form.value = buildDefaultForm();
};

const hydrateTemplates = inbox => {
  templates.value = Array.isArray(inbox?.message_templates) ? inbox.message_templates : [];
};

const componentTypeMatches = (component, type) =>
  component?.type?.toString()?.toUpperCase() === type;

const templateKind = template => {
  const buttonsComponent = Array(template?.components).find(component =>
    componentTypeMatches(component, 'BUTTONS')
  );
  const buttonTypes = Array(buttonsComponent?.buttons).map(button =>
    button?.type?.toString()?.toUpperCase()
  );

  if (buttonTypes.includes('CATALOG')) return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE_CATALOG');
  if (buttonTypes.includes('MPM')) return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE_MULTI_PRODUCT');

  return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE_STANDARD');
};

const templateComponent = (template, type) =>
  Array(template?.components).find(component => componentTypeMatches(component, type));

const templateHeaderLabel = template => {
  const header = templateComponent(template, 'HEADER');
  if (!header) return '';
  if (header.format === 'TEXT') return header.text || '';
  return header.format || '';
};

const templateBodyText = template => templateComponent(template, 'BODY')?.text || '';

const templateFooterText = template => templateComponent(template, 'FOOTER')?.text || '';

const templateButtons = template =>
  Array(templateComponent(template, 'BUTTONS')?.buttons).filter(button => button?.text);

const isTemplateExpanded = template =>
  expandedTemplateName.value === `${template?.name}-${template?.id || 'template'}`;

const toggleTemplatePreview = template => {
  const key = `${template?.name}-${template?.id || 'template'}`;
  expandedTemplateName.value = expandedTemplateName.value === key ? '' : key;
};

const openCreateForm = async () => {
  resetForm();
  editingTemplateId.value = '';
  showCreateForm.value = true;
  await nextTick();
  createFormRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const closeCreateForm = () => {
  showCreateForm.value = false;
  editingTemplateId.value = '';
  resetForm();
};

const parseTemplateIntoForm = template => {
  const header = templateComponent(template, 'HEADER');
  const body = templateComponent(template, 'BODY');
  const footer = templateComponent(template, 'FOOTER');
  const buttonsComponent = templateComponent(template, 'BUTTONS');
  const buttonTypes = Array(buttonsComponent?.buttons).map(button =>
    button?.type?.toString()?.toUpperCase()
  );

  let templateMode = 'standard';
  if (buttonTypes.includes('CATALOG')) templateMode = 'catalog';
  if (buttonTypes.includes('MPM')) templateMode = 'multi_product';

  form.value = {
    templateMode,
    name: template?.name || '',
    category: template?.category || 'UTILITY',
    language: template?.language || 'es_AR',
    customLanguage: '',
    body: body?.text || '',
    bodyExamplesText: Array(body?.example?.body_text || [])
      .flat()
      .filter(Boolean)
      .join('\n'),
    footer: footer?.text || '',
    headerType: header?.format || '',
    headerText: header?.text || '',
    headerExampleHandle: Array(header?.example?.header_handle || [])
      .flat()
      .filter(Boolean)
      .join('\n'),
    headerExamplesText: Array(header?.example?.header_text || []).flat().join('\n'),
    catalogButtonText:
      Array(buttonsComponent?.buttons).find(button => button?.type === 'CATALOG')?.text ||
      'Ver catalogo',
    multiProductButtonText:
      Array(buttonsComponent?.buttons).find(button => button?.type === 'MPM')?.text ||
      'Ver productos',
    buttons:
      templateMode === 'standard'
        ? Array(buttonsComponent?.buttons || []).map(button => ({
            type: button?.type || 'QUICK_REPLY',
            text: button?.text || '',
            url: button?.url || '',
            phone_number: button?.phone_number || '',
            example: button?.example || '',
          }))
        : [],
  };
};

const startEditingTemplate = async template => {
  parseTemplateIntoForm(template);
  editingTemplateId.value = template?.id || '';
  showCreateForm.value = true;
  await nextTick();
  createFormRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const loadTemplates = async () => {
  isLoading.value = true;
  loadError.value = '';

  try {
    const response = await InboxesAPI.getWhatsAppTemplates(props.inbox.id);
    templates.value = Array.isArray(response.data?.payload) ? response.data.payload : [];
    hasLoaded.value = true;
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_LOAD_ERROR');
    useAlert(loadError.value);
  } finally {
    isLoading.value = false;
  }
};

const createTemplate = async () => {
  if (!canCreateTemplate.value) return;

  isSubmitting.value = true;
  const isEditing = !!editingTemplateId.value;
  try {
    const payload = {
      name: form.value.name,
      category: form.value.category,
      language: selectedLanguage.value,
      body: form.value.body,
      footer: form.value.footer,
      body_examples: bodyExamples.value,
      header: {
        type: form.value.headerType,
        text: form.value.headerText,
        example_handle: form.value.headerExampleHandle,
        examples: headerExamples.value,
      },
      buttons: effectiveButtons.value,
    };

    const response = isEditing
      ? await InboxesAPI.updateWhatsAppTemplate(
          props.inbox.id,
          editingTemplateId.value,
          payload
        )
      : await InboxesAPI.createWhatsAppTemplate(props.inbox.id, payload);
    templates.value = Array.isArray(response.data?.payload) ? response.data.payload : templates.value;
    hasLoaded.value = true;
    closeCreateForm();
    useAlert(
      isEditing
        ? t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_UPDATE_SUCCESS')
        : t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CREATE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        (isEditing
          ? t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_UPDATE_ERROR')
          : t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CREATE_ERROR'))
    );
  } finally {
    isSubmitting.value = false;
  }
};

const deleteTemplate = async template => {
  const confirmed = window.confirm(
    t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_DELETE_CONFIRM', {
      name: template?.name,
    })
  );
  if (!confirmed) return;

  deletingTemplateName.value = template?.name || '';
  try {
    const response = await InboxesAPI.deleteWhatsAppTemplate(props.inbox.id, {
      name: template?.name,
      hsm_id: template?.id,
    });
    templates.value = Array.isArray(response.data?.payload) ? response.data.payload : [];
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_DELETE_ERROR')
    );
  } finally {
    deletingTemplateName.value = '';
  }
};

const addButton = () => {
  form.value.buttons = [...form.value.buttons, createDefaultButton()];
};

const removeButton = index => {
  form.value.buttons = form.value.buttons.filter((_, idx) => idx !== index);
};

watch(
  () => props.inbox,
  inbox => {
    hydrateTemplates(inbox);
  },
  { immediate: true, deep: true }
);

watch(
  () => props.active,
  isActive => {
    if (isActive) loadTemplates();
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-11">
      {{
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_STATUS_NOTE', {
          count: templates.length,
        })
      }}
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <Button
        type="button"
        color="blue"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_REFRESH')"
        :is-loading="isLoading"
        @click="loadTemplates"
      />
      <Button
        type="button"
        color="blue"
        icon="i-ph-plus"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_NEW')"
        @click="openCreateForm"
      />
    </div>

    <div
      v-if="loadError"
      class="rounded-xl border border-n-ruby-6 bg-n-ruby-3/20 px-4 py-4 text-sm text-n-ruby-11"
    >
      <p class="mb-1 font-medium">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_LOAD_ERROR') }}
      </p>
      <p class="mb-0 break-words">{{ loadError }}</p>
    </div>

    <div
      v-if="showCreateForm"
      ref="createFormRef"
      class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4"
    >
      <div class="flex items-center justify-between gap-3">
        <div>
          <p class="mb-1 text-sm font-semibold text-n-slate-12">
            {{
              editingTemplateId
                ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_EDIT')
                : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_NEW')
            }}
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_FULL_NOTE') }}
          </p>
        </div>
        <Button
          type="button"
          variant="faded"
          color="slate"
          icon="i-ph-x"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CANCEL_CREATE')"
          @click="closeCreateForm"
        />
      </div>

      <div class="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(18rem,0.9fr)]">
        <div class="grid gap-4">
          <div class="grid gap-4 lg:grid-cols-2">
            <label class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_NAME') }}
              </span>
              <input
                v-model="form.name"
                type="text"
                class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                :placeholder="
                  $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_NAME_PLACEHOLDER')
                "
              />
            </label>

            <label class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_LANGUAGE') }}
              </span>
              <select
                v-model="form.language"
                class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option
                  v-for="option in languageOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>

            <label class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY') }}
              </span>
              <select
                v-model="form.category"
                class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option
                  v-for="option in categoryOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ $t(option.labelKey) }}
                </option>
              </select>
            </label>

            <label class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE') }}
              </span>
              <select
                v-model="form.templateMode"
                class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option
                  v-for="option in templateModeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ $t(option.labelKey) }}
                </option>
              </select>
            </label>

            <label
              v-if="form.language === 'custom'"
              class="grid gap-1 text-sm text-n-slate-12 lg:col-span-2"
            >
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_LANGUAGE_PLACEHOLDER') }}
              </span>
              <input
                v-model="form.customLanguage"
                type="text"
                class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                :placeholder="
                  $t(
                    'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_LANGUAGE_PLACEHOLDER'
                  )
                "
              />
            </label>
          </div>

          <div class="grid gap-3 rounded-xl border border-n-container bg-n-background p-4">
            <div class="grid gap-3 lg:grid-cols-2">
              <label class="grid gap-1 text-sm text-n-slate-12">
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_HEADER_TYPE') }}
                </span>
                <select
                  v-model="form.headerType"
                  class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                >
                  <option value="">
                    {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_HEADER_NONE') }}
                  </option>
                  <option value="TEXT">Text</option>
                  <option value="IMAGE">Image</option>
                  <option value="VIDEO">Video</option>
                  <option value="DOCUMENT">Document</option>
                </select>
              </label>

              <label
                v-if="form.headerType === 'TEXT'"
                class="grid gap-1 text-sm text-n-slate-12"
              >
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_HEADER_TEXT') }}
                </span>
                <input
                  v-model="form.headerText"
                  type="text"
                  class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                />
              </label>

              <label
                v-if="form.headerType && form.headerType !== 'TEXT'"
                class="grid gap-1 text-sm text-n-slate-12"
              >
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_HEADER_HANDLE') }}
                </span>
                <input
                  v-model="form.headerExampleHandle"
                  type="text"
                  class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                />
              </label>

              <label
                v-if="form.headerType === 'TEXT'"
                class="grid gap-1 text-sm text-n-slate-12 lg:col-span-2"
              >
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_HEADER_EXAMPLE') }}
                </span>
                <textarea
                  v-model="form.headerExamplesText"
                  rows="2"
                  class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BODY_EXAMPLES_PLACEHOLDER'
                    )
                  "
                />
              </label>
            </div>

            <label class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BODY') }}
              </span>
              <textarea
                v-model="form.body"
                rows="5"
                class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                :placeholder="
                  $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BODY_PLACEHOLDER')
                "
              />
            </label>

            <label class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BODY_EXAMPLES') }}
              </span>
              <textarea
                v-model="form.bodyExamplesText"
                rows="3"
                class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                :placeholder="
                  $t(
                    'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BODY_EXAMPLES_PLACEHOLDER'
                  )
                "
              />
            </label>

            <label class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_FOOTER') }}
              </span>
              <textarea
                v-model="form.footer"
                rows="2"
                class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                :placeholder="
                  $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_FOOTER_PLACEHOLDER')
                "
              />
            </label>
          </div>

          <div
            v-if="form.templateMode === 'standard'"
            class="grid gap-3 rounded-xl border border-n-container bg-n-background p-4"
          >
            <div class="flex flex-wrap items-center justify-between gap-3">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTONS') }}
              </p>
              <Button
                type="button"
                variant="faded"
                color="slate"
                icon="i-ph-plus"
                :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_ADD')"
                @click="addButton"
              />
            </div>

            <div v-if="!form.buttons.length" class="text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTONS_EMPTY') }}
            </div>

            <div
              v-for="(button, index) in form.buttons"
              :key="`button-${index}`"
              class="grid gap-3 rounded-xl border border-n-container bg-white p-3"
            >
              <div class="grid gap-3 lg:grid-cols-2">
                <label class="grid gap-1 text-sm text-n-slate-12">
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_TYPE') }}
                  </span>
                  <select
                    v-model="button.type"
                    class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  >
                    <option
                      v-for="option in buttonTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ $t(option.labelKey) }}
                    </option>
                  </select>
                </label>

                <label class="grid gap-1 text-sm text-n-slate-12">
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_TEXT') }}
                  </span>
                  <input
                    v-model="button.text"
                    type="text"
                    class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </label>

                <label
                  v-if="button.type === 'URL'"
                  class="grid gap-1 text-sm text-n-slate-12"
                >
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_URL_FIELD') }}
                  </span>
                  <input
                    v-model="button.url"
                    type="text"
                    class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </label>

                <label
                  v-if="button.type === 'PHONE_NUMBER'"
                  class="grid gap-1 text-sm text-n-slate-12"
                >
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_PHONE_FIELD') }}
                  </span>
                  <input
                    v-model="button.phone_number"
                    type="text"
                    class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </label>

                <label
                  v-if="button.type === 'URL' || button.type === 'COPY_CODE'"
                  class="grid gap-1 text-sm text-n-slate-12"
                >
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_EXAMPLE') }}
                  </span>
                  <input
                    v-model="button.example"
                    type="text"
                    class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </label>
              </div>

              <div class="flex justify-end">
                <Button
                  type="button"
                  variant="faded"
                  color="slate"
                  icon="i-lucide-trash-2"
                  :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_REMOVE')"
                  @click="removeButton(index)"
                />
              </div>
            </div>
          </div>

          <div
            v-else
            class="grid gap-3 rounded-xl border border-n-container bg-n-background p-4"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                form.templateMode === 'catalog'
                  ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATALOG_NOTE')
                  : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MULTI_PRODUCT_NOTE')
              }}
            </p>

            <label
              v-if="form.templateMode === 'catalog'"
              class="grid gap-1 text-sm text-n-slate-12"
            >
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_TEXT') }}
              </span>
              <input
                v-model="form.catalogButtonText"
                type="text"
                class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              />
            </label>

            <label v-else class="grid gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_BUTTON_TEXT') }}
              </span>
              <input
                v-model="form.multiProductButtonText"
                type="text"
                class="w-full rounded-lg border border-n-container bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              />
            </label>
          </div>
        </div>

        <div class="grid gap-3">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_PREVIEW') }}
          </p>
          <div class="rounded-[1.5rem] border border-n-container bg-[#e9ded6] p-3">
            <div class="rounded-[1.25rem] bg-white p-4 shadow-sm">
              <div class="mb-3 flex flex-wrap items-center gap-2">
                <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11">
                  {{ form.category }}
                </span>
                <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11">
                  {{ selectedLanguageLabel }}
                </span>
                <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11">
                  {{
                    $t(
                      templateModeOptions.find(option => option.value === form.templateMode)
                        ?.labelKey || 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MODE_STANDARD'
                    )
                  }}
                </span>
              </div>

              <p v-if="previewHeaderLabel" class="mb-2 text-sm font-semibold text-n-slate-12">
                {{ previewHeaderLabel }}
              </p>

              <p class="mb-2 whitespace-pre-wrap text-sm text-n-slate-12">
                {{ form.body.trim() || '...' }}
              </p>

              <p
                v-if="form.footer.trim()"
                class="mb-3 whitespace-pre-wrap text-xs text-n-slate-10"
              >
                {{ form.footer.trim() }}
              </p>

              <div
                v-if="effectiveButtons.length"
                class="flex flex-col gap-2 border-t border-n-container pt-3"
              >
                <button
                  v-for="(button, index) in effectiveButtons"
                  :key="`preview-button-${index}`"
                  type="button"
                  class="rounded-xl border border-n-container bg-n-alpha-2 px-3 py-2 text-left text-sm text-n-brand"
                >
                  {{ button.text }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button
          type="button"
          variant="faded"
          color="slate"
          icon="i-ph-x"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CANCEL_CREATE')"
          @click="closeCreateForm"
        />
        <Button
          type="button"
          color="blue"
          icon="i-ph-paper-plane-tilt"
          :label="
            editingTemplateId
              ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_SAVE_BUTTON')
              : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CREATE_BUTTON')
          "
          :disabled="!canCreateTemplate"
          :is-loading="isSubmitting"
          @click="createTemplate"
        />
      </div>
    </div>

    <div class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4">
      <div class="grid gap-4 lg:grid-cols-3">
        <label class="grid gap-1 text-sm text-n-slate-12">
          <span class="font-medium">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_SEARCH') }}
          </span>
          <input
            v-model="searchQuery"
            type="text"
            class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_SEARCH_PLACEHOLDER')
            "
          />
        </label>

        <label class="grid gap-1 text-sm text-n-slate-12">
          <span class="font-medium">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY') }}
          </span>
          <select
            v-model="categoryFilter"
            class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="all">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_STATUS_ALL') }}
            </option>
            <option value="utility">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY_UTILITY') }}
            </option>
            <option value="marketing">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY_MARKETING') }}
            </option>
            <option value="authentication">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_CATEGORY_AUTHENTICATION') }}
            </option>
          </select>
        </label>

        <label class="grid gap-1 text-sm text-n-slate-12">
          <span class="font-medium">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_STATUS_FILTER') }}
          </span>
          <select
            v-model="statusFilter"
            class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="all">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_STATUS_ALL') }}
            </option>
            <option value="approved">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_STATUS_APPROVED') }}
            </option>
            <option value="pending">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_STATUS_PENDING') }}
            </option>
            <option value="rejected">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_STATUS_REJECTED') }}
            </option>
          </select>
        </label>
      </div>

      <div
        v-if="isLoading"
        class="rounded-xl border border-dashed border-n-container bg-n-background px-4 py-8 text-center text-sm text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_LOADING') }}
      </div>

      <div
        v-else-if="!filteredTemplates.length"
        class="rounded-xl border border-dashed border-n-container bg-n-background px-4 py-8 text-center text-sm text-n-slate-11"
      >
        {{
          hasLoaded
            ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_EMPTY')
            : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_LOADING')
        }}
      </div>

      <div v-else class="grid gap-3">
        <div
          v-for="template in filteredTemplates"
          :key="`${template.name}-${template.id || 'template'}`"
          class="rounded-xl border border-n-container bg-n-background p-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="mb-1 truncate text-sm font-semibold text-n-slate-12">
                {{ template.name }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ templateKind(template) }} · {{ template.category }} · {{ template.language }} ·
                {{ template.status }}
              </p>
            </div>

            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-pencil-simple"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_EDIT')"
              @click="startEditingTemplate(template)"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              :icon="
                isTemplateExpanded(template)
                  ? 'i-ph-eye-slash'
                  : 'i-ph-eye'
              "
              :label="
                isTemplateExpanded(template)
                  ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_HIDE_PREVIEW')
                  : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_VIEW_PREVIEW')
              "
              @click="toggleTemplatePreview(template)"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-lucide-trash-2"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_DELETE')"
              :is-loading="deletingTemplateName === template.name"
              :disabled="deletingTemplateName === template.name"
              @click="deleteTemplate(template)"
            />
          </div>

          <div
            v-if="isTemplateExpanded(template)"
            class="mt-4 rounded-[1.25rem] border border-n-container bg-[#e9ded6] p-3"
          >
            <div class="rounded-[1rem] bg-white p-4 shadow-sm">
              <div class="mb-3 flex flex-wrap items-center gap-2">
                <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11">
                  {{ templateKind(template) }}
                </span>
                <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11">
                  {{ template.language }}
                </span>
                <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11">
                  {{ template.status }}
                </span>
              </div>

              <p
                v-if="templateHeaderLabel(template)"
                class="mb-2 text-sm font-semibold text-n-slate-12"
              >
                {{ templateHeaderLabel(template) }}
              </p>

              <p class="mb-2 whitespace-pre-wrap text-sm text-n-slate-12">
                {{ templateBodyText(template) || '...' }}
              </p>

              <p
                v-if="templateFooterText(template)"
                class="mb-3 whitespace-pre-wrap text-xs text-n-slate-10"
              >
                {{ templateFooterText(template) }}
              </p>

              <div
                v-if="templateButtons(template).length"
                class="flex flex-col gap-2 border-t border-n-container pt-3"
              >
                <button
                  v-for="(button, index) in templateButtons(template)"
                  :key="`template-preview-button-${index}`"
                  type="button"
                  class="rounded-xl border border-n-container bg-n-alpha-2 px-3 py-2 text-left text-sm text-n-brand"
                >
                  {{ button.text }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
